import React, {
  useRef,
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from 'react';
import { createPortal } from 'react-dom';
import { Flex, Stack } from '@grubhubprod/gh-cookbook-react/dist';
import { Copy, SvgCaretDown, SvgCaretUp } from '@grubhubprod/gh-cookbook-react';
import classnames from 'classnames';
import { Dropdown } from 'src/components/Dropdown/Dropdown';
import SelectSearchMenu from 'src/components/SelectSearch/SelectSearchMenu';
import {
  createSelectedItemsMap,
  getInitValue,
  getSearchMenuStyles,
} from 'src/components/SelectSearch/utils';
import { ClearButton } from 'src/components/SelectSearch/ClearButton';
import {
  SearchResourceOptions,
  MAX_PAGE_SIZE,
} from 'src/utilities/useSearchHooks';
import { useSelectSearchMenuVisibility } from 'src/components/SelectSearch/hooks/useSelectSearchMenuVisibility';

export interface SelectSearchProps<T, M> {
  corpId?: string;
  keyName: keyof T; // this should almost always be "id"
  displayProp: keyof T;
  searchInputPlaceholder?: string;
  placeholder?: string;
  initialSelectedValue?: M extends true ? Partial<T>[] : Partial<T>;
  isMultiSelect?: boolean;
  isRequiredDepartmentSearch?: boolean; // hacky way to prevent "Unassigned" department
  useSearchToInitalizeSelectedItems?: boolean; // use this when you can only provide an ID of the selectedValue to have the component handle getting the remaining info
  onValueChange?: (value: M extends true ? T[] : T) => void;
  searchResource?: SearchResourceOptions;
  selectedItemLabel?: string;
  selectedItemPluralLabel?: string;
  maxHeight?: string;
  filterSearchResults?: (item: T) => boolean;
  getNoResultsMessage?: (query: string) => string | JSX.Element;
  inputSize?: 'small' | 'medium';
  error?: boolean;
}

export const SelectSearch = forwardRef(SelectSearchInput) as <T, M>(
  props: SelectSearchProps<T, M> & {
    ref?: React.ForwardedRef<HTMLElement>;
  }
) => ReturnType<typeof SelectSearchInput>;

function SelectSearchInput<T, M>(
  {
    displayProp,
    searchResource,
    initialSelectedValue = null,
    onValueChange,
    placeholder,
    keyName,
    isMultiSelect,
    isRequiredDepartmentSearch,
    useSearchToInitalizeSelectedItems,
    searchInputPlaceholder,
    selectedItemLabel,
    selectedItemPluralLabel,
    maxHeight,
    corpId,
    inputSize,
    filterSearchResults,
    getNoResultsMessage,
    error = false,
  }: SelectSearchProps<T, M>,
  ref
): JSX.Element {
  const [selectedItems, setSelectedItems] = useState<(Partial<T> | T)[]>(
    getInitValue<Partial<T>>(initialSelectedValue)
  );
  const dropdownContainerRef = useRef(null);
  const dropdown = useRef(null);
  const selectSearchMenuRef = useRef(null);
  const isInitialized = useRef(false);
  const selectedItemsIdMap = useRef<{ [id: string]: boolean }>({});
  const { doSearch, resultsList } = searchResource(corpId);

  useSelectSearchMenuVisibility(selectSearchMenuRef, dropdown);

  function onValueChangeHandler(value) {
    setSelectedItems(isMultiSelect ? value : [value]);
    if (!isMultiSelect) {
      dropdown.current.close();
    }
    if (onValueChange) {
      onValueChange(value);
    }
  }

  // set the initial value when a truthy selected value is provided
  useEffect(() => {
    if (
      !isInitialized.current &&
      initialSelectedValue &&
      !useSearchToInitalizeSelectedItems
    ) {
      setSelectedItems(getInitValue(initialSelectedValue));
      isInitialized.current = true;
    }
  }, [initialSelectedValue, isInitialized, useSearchToInitalizeSelectedItems]);

  // If we know a resource is restricted to having less items than our MAX_PAGE_SIZE (see const value in useSearchHook.ts)
  // we can use a simple search call to match the incomplete items to the info from the search call since we
  // will be guaranteed to have all of the possible items in one search call.
  useEffect(() => {
    if (
      useSearchToInitalizeSelectedItems &&
      !isInitialized.current &&
      selectedItems.length > 0
    ) {
      createSelectedItemsMap<T>({
        keyName,
        useSearchToInitalizeSelectedItems,
        selectedItemsIdMap,
        selectedItems,
      });
      const hasAllNecessaryInfo = selectedItems.every((item) => {
        const hasDisplayValue = item[displayProp];

        return hasDisplayValue;
      });

      if (!hasAllNecessaryInfo) {
        doSearch({ size: MAX_PAGE_SIZE });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedItems,
    useSearchToInitalizeSelectedItems,
    isInitialized,
    displayProp,
  ]);

  useEffect(() => {
    if (
      !isInitialized.current &&
      Object.keys(selectedItemsIdMap.current).length > 0 &&
      resultsList.length > 0
    ) {
      const fullInfoSelectedItems = (resultsList as unknown as T[]).filter(
        (result) => {
          const resultKeyValue = result[keyName] as unknown as string;

          return selectedItemsIdMap.current[resultKeyValue];
        }
      );

      if (fullInfoSelectedItems.length > 0) {
        setSelectedItems(fullInfoSelectedItems);
      }

      isInitialized.current = true;
    }
  }, [isInitialized, keyName, resultsList, selectedItemsIdMap]);

  useImperativeHandle(ref, () => ({
    reset: () => setSelectedItems([]),
  }));

  const clearSelectedItems = (): void => {
    onValueChangeHandler([]);
    setSelectedItems([]);
  };

  return (
    <Stack position="relative" ref={dropdownContainerRef}>
      <Dropdown
        data-testid="dropdown-menu"
        error={error}
        isFullWidth
        isPortalMenu
        maxMenuHeight={maxHeight}
        ref={dropdown}
        closeMenuOnClick={false}
        toggle={({ expanded }): JSX.Element => (
          <Stack
            data-testid="search-select"
            backgroundColor="globals.white"
            className={classnames('input', {
              small: inputSize === 'small',
              hasError: error,
            })}
          >
            <Flex
              height="100%"
              width="100%"
              alignItems="center"
              justifyContent="space-between"
            >
              <Stack x="x-sm" style={{ overflow: 'hidden' }} width="100%">
                {isMultiSelect ? (
                  <>
                    {selectedItems && selectedItems.length > 0 ? (
                      <Flex
                        justifyContent="space-between"
                        flexWrap="nowrap"
                        width="100%"
                        alignItems="center"
                      >
                        <Stack x="xxx-sm" className="ellipsisOverflow">
                          <Copy variant="body" color="primaryText">
                            {selectedItems.length === 1 ? (
                              <span>1 {selectedItemLabel}</span>
                            ) : (
                              <span>
                                {selectedItems.length} {selectedItemPluralLabel}
                              </span>
                            )}
                          </Copy>
                        </Stack>
                        <ClearButton onClick={clearSelectedItems} />
                      </Flex>
                    ) : (
                      <Copy variant="body" color="secondaryText">
                        {placeholder || 'Search'}
                      </Copy>
                    )}
                  </>
                ) : (
                  <div
                    data-testid="single-selected-item"
                    style={{
                      textOverflow: 'ellipsis',
                      overflow: 'hidden',
                    }}
                  >
                    {selectedItems && selectedItems[0] ? (
                      selectedItems[0][displayProp]
                    ) : (
                      <Copy variant="body" color="secondaryText">
                        {placeholder || 'Search'}
                      </Copy>
                    )}
                  </div>
                )}
              </Stack>
              <Flex color="secondaryText">
                {expanded ? (
                  <SvgCaretUp size="16px" />
                ) : (
                  <SvgCaretDown size="16px" />
                )}
              </Flex>
            </Flex>
          </Stack>
        )}
        menu={({ show }): JSX.Element => {
          if (!show) return null;

          const styles = getSearchMenuStyles(dropdownContainerRef);

          return (
            <>
              {createPortal(
                <SelectSearchMenu<T, M>
                  corpId={corpId}
                  keyName={keyName}
                  displayProp={displayProp}
                  isRequiredDepartmentSearch={isRequiredDepartmentSearch}
                  searchInputPlaceholder={searchInputPlaceholder}
                  selectedItems={selectedItems as T[]}
                  updateSelectedItems={(selectedItems) =>
                    setSelectedItems(selectedItems)
                  }
                  isMultiSelect={isMultiSelect}
                  onValueChange={onValueChangeHandler}
                  searchResource={searchResource}
                  filterSearchResults={filterSearchResults}
                  getNoResultsMessage={getNoResultsMessage}
                  style={styles}
                  maxMenuHeight={maxHeight}
                  ref={selectSearchMenuRef}
                />,
                document.body
              )}
            </>
          );
        }}
      />
    </Stack>
  );
}
